import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, pointerWithin, useSensor, useSensors, type DragEndEvent, type DragStartEvent, type Modifier } from "@dnd-kit/core";
import { getEventCoordinates } from "@dnd-kit/utilities";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import type { ProjectSummary } from "@/hooks/useProjectBoardData";
import type { Tables } from "@/types/database.types";
import { useQueryParams } from "@/hooks/useQueryParams";
import { db } from "@/lib/supabase";
import { diffRecords, recordAudit } from "@/lib/audit";
import {
  categorizeProjects,
  computeEmployeeActiveProjectLabels,
  computeOverAllocatedEmployeeIds,
  filterProjectsForBoard,
} from "@/utils/projectUtils";
import { useCategories } from "@/contexts/CategoriesContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectRow } from "./ProjectRow";
import { ProjectSection } from "./ProjectSection";
import { StickyProjectHeaderProvider } from "./StickyProjectHeaderContext";
import type { ProjectAvailabilityFocus } from "./ProjectAvailabilityDock";

// The sticky line below the app header (--app-header-height, 5rem) and the
// floating controls bar (--floating-controls-height, 3rem) in src/index.css.
// Must match FLOATING_TOP_PX in StickyProjectHeaderContext.tsx.
const FLOATING_TOP_PX = 128;
const CURSOR_GRIP_INSET_PX = 12;

/**
 * When the dragged project's original row has scrolled above the floating
 * controls bar, the drag preview would otherwise appear offset far from the
 * cursor (anchored to the off-screen original). Snap the overlay's top-left
 * to a small inset from the cursor's initial position so it stays near the
 * grip handle the user pressed in the sticky floating header.
 */
const snapOverlayToCursorWhenOffscreen: Modifier = ({
  activatorEvent,
  activeNodeRect,
  draggingNodeRect,
  transform,
}) => {
  if (!activeNodeRect || activeNodeRect.top >= FLOATING_TOP_PX) return transform;
  if (!draggingNodeRect || !activatorEvent) return transform;
  const cursorInitial = getEventCoordinates(activatorEvent);
  if (!cursorInitial) return transform;
  const adjustX = (cursorInitial.x - CURSOR_GRIP_INSET_PX) - draggingNodeRect.left;
  const adjustY = (cursorInitial.y - CURSOR_GRIP_INSET_PX) - draggingNodeRect.top;
  return { ...transform, x: transform.x + adjustX, y: transform.y + adjustY };
};

interface ProjectBoardProps {
  projects: ProjectSummary[];
  loading?: boolean;
  refresh: () => void;
  addProjectOptimistically: (project: ProjectSummary) => void;
  confirmProjectOptimistically: (
    projectId: string,
    project: Tables<"projects">
  ) => void;
  removeProjectOptimistically: (projectId: string) => void;
  updateProjectOptimistically: (projectId: string, updates: Partial<ProjectSummary>) => void;
  refreshSilently: () => Promise<void>;
  searchTerms: string[];
  selectedOffices: string[];
  expandedProjects: Record<string, boolean>;
  expandProject: (projectId: string) => void;
  ensureProjectExpanded: (projectId: string) => void;
  timelineStartDate: string;
  today: string;
  onOpenAvailabilityForAssignment: (focus: ProjectAvailabilityFocus) => void;
  focusedAvailabilityAssignmentId: string | null;
  /** Which collapsed-by-default categories the user has revealed via the controls toggle. */
  revealedCategories: Record<string, boolean>;
}

/**
 * ProjectBoard component - displays project data with filtering and grouping
 * This component handles data logic and composition, layout is handled by the page
 */
export function ProjectBoard({
  projects,
  loading = false,
  refresh,
  addProjectOptimistically,
  confirmProjectOptimistically,
  removeProjectOptimistically,
  updateProjectOptimistically,
  refreshSilently,
  searchTerms,
  selectedOffices,
  expandedProjects,
  expandProject,
  ensureProjectExpanded,
  timelineStartDate,
  today,
  onOpenAvailabilityForAssignment,
  focusedAvailabilityAssignmentId,
  revealedCategories,
}: ProjectBoardProps) {
  const { get } = useQueryParams();
  const focusedProjectId = get("project");
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const { categories, byId: categoriesById } = useCategories();

  useEffect(() => {
    if (!focusedProjectId || loading) return;
    ensureProjectExpanded(focusedProjectId);
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-project-id="${focusedProjectId}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    return () => cancelAnimationFrame(id);
  }, [focusedProjectId, loading, ensureProjectExpanded]);

  const filteredProjects = useMemo(() => {
    return filterProjectsForBoard(projects, searchTerms, selectedOffices, focusedProjectId);
  }, [projects, searchTerms, selectedOffices, focusedProjectId]);

  const { sections } = useMemo(
    () => categorizeProjects(filteredProjects, categories, focusedProjectId, today),
    [filteredProjects, categories, focusedProjectId, today]
  );

  // When a project is deep-linked (?project=ID) into a collapsed-by-default
  // section, reveal that section so the project is visible even if hidden.
  const focusedCategoryId = useMemo(() => {
    if (!focusedProjectId) return null;
    return filteredProjects.find((p) => p.id === focusedProjectId)?.category_id ?? null;
  }, [filteredProjects, focusedProjectId]);

  const overAllocatedEmployeeIds = useMemo(
    () => computeOverAllocatedEmployeeIds(projects, new Date(timelineStartDate)),
    [projects, timelineStartDate]
  );

  const employeeProjectLabels = useMemo(
    () => computeEmployeeActiveProjectLabels(
      projects,
      new Date(timelineStartDate),
      today,
    ),
    [projects, timelineStartDate, today]
  );

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const activeProject = activeDragId
    ? filteredProjects.find((p) => p.id === activeDragId) ?? null
    : null;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const projectId = String(active.id);
    const newCategoryId = String(over.id);
    const currentCategoryId = filteredProjects.find((p) => p.id === projectId)?.category_id;

    if (currentCategoryId === newCategoryId) return;

    updateProjectOptimistically(projectId, { category_id: newCategoryId });

    markSelfEdit("projects", projectId);
    const { error } = await db.projects.update(projectId, { category_id: newCategoryId });
    if (error) {
      console.error("Failed to update project category:", error);
    } else {
      const draggedProject = filteredProjects.find((p) => p.id === projectId);
      recordAudit({
        table: "projects",
        recordId: projectId,
        operation: "UPDATE",
        changes: diffRecords(
          { category: currentCategoryId ? categoriesById.get(currentCategoryId)?.name ?? null : null },
          { category: categoriesById.get(newCategoryId)?.name ?? null },
        ),
        recordLabel: draggedProject?.name ?? null,
        officeId: draggedProject?.office_id ?? null,
      });
    }
    await refreshSilently();
  }, [filteredProjects, updateProjectOptimistically, refreshSilently, markSelfEdit, categoriesById]);

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
    <StickyProjectHeaderProvider>
    <div className="board-content">
      {sections.map(({ category, projects: sectionProjects }) => {
        // Collapsed-by-default sections (e.g. Sold - Active) stay hidden until the
        // user reveals them via the controls toggle, or a project in them is deep-linked.
        const isHidden =
          category.collapsed_by_default &&
          !revealedCategories[category.id] &&
          category.id !== focusedCategoryId;
        if (isHidden) return null;

        return (
          <ProjectSection
            key={category.id}
            title={category.name}
            count={sectionProjects.length}
            defaultDealStageId={category.default_deal_stage}
            defaultCategory={category.id}
            droppableId={category.id}
            onOptimisticCreate={addProjectOptimistically}
            onOptimisticConfirm={confirmProjectOptimistically}
            onOptimisticError={removeProjectOptimistically}
          >
            {sectionProjects.map((project) => (
              <ProjectRow key={project.id} project={project}
                timelineStartDate={timelineStartDate}
                onRefresh={refresh}
                updateProjectOptimistically={updateProjectOptimistically}
                refreshSilently={refreshSilently}
                overAllocatedEmployeeIds={overAllocatedEmployeeIds}
                employeeProjectLabels={employeeProjectLabels}
                expanded={!!expandedProjects[project.id]}
                onToggleExpanded={expandProject}
                onOpenAvailabilityForAssignment={onOpenAvailabilityForAssignment}
                focusedAvailabilityAssignmentId={focusedAvailabilityAssignmentId} />
            ))}
          </ProjectSection>
        );
      })}
    </div>
    </StickyProjectHeaderProvider>
    <DragOverlay modifiers={[snapOverlayToCursorWhenOffscreen]}>
      {activeProject ? (
        <div className="project-card--dragging">
          {activeProject.name}
        </div>
      ) : null}
    </DragOverlay>
    </DndContext>
  );
}
